// client/src/features/documents/mine-search.ts — „Documentele mele"'s URL state (D-2570).
//
// §9.2.3 is normative for every LIST screen: which view, which apartment, which shelf and what
// was typed in the search box live in the URL, so a filtered list survives a refresh, travels
// in a link and Back undoes a filter. None of it reaches the server — the list arrives whole and
// is narrowed here (`./mine.ts`) — so the names are this screen's own, not an endpoint's.

import { z } from 'zod';

import { DOCUMENT_KINDS } from './enums';
import { EXPIRY_HORIZON_DAYS } from './expiring';
import { OWNER_DOCUMENT_AUTHORS, OWNER_DOCUMENT_CATEGORIES } from './mine';

/**
 * The three readings of one list. „Pe apartamente" is the default: an owner opens the tab to find
 * a paper of one flat, and the flat is the first thing they know about it.
 */
export const MY_DOCUMENT_VIEWS = ['apartments', 'all', 'expiring'] as const;

export type MyDocumentsView = (typeof MY_DOCUMENT_VIEWS)[number];

export const myDocumentsSearchSchema = z.object({
  view: z.enum(MY_DOCUMENT_VIEWS).default('apartments'),
  /** An apartment's id, or `personal` for the owner's own papers. Absent: the first apartment. */
  place: z.string().optional(),
  category: z.enum(OWNER_DOCUMENT_CATEGORIES).optional(),
  author: z.enum(OWNER_DOCUMENT_AUTHORS).optional(),
  kind: z.enum(DOCUMENT_KINDS).optional(),
  q: z.string().optional(),
  /** The „expiră în curând" horizon, in days — the vault ladder's rungs. */
  expiringWithinDays: z.coerce.number().int().min(0).max(365).default(EXPIRY_HORIZON_DAYS),
});

export type MyDocumentsSearch = z.infer<typeof myDocumentsSearchSchema>;

/** Whether any narrowing of the „Toate" view is engaged — the empty state needs to know why. */
export function hasMyDocumentFilters(search: MyDocumentsSearch): boolean {
  return (
    search.place !== undefined ||
    search.category !== undefined ||
    search.author !== undefined ||
    search.kind !== undefined ||
    (search.q !== undefined && search.q.trim() !== '')
  );
}

/** Clearing keeps the view and the horizon, which are what the screen IS rather than filters. */
export function clearedMyDocuments(search: MyDocumentsSearch): MyDocumentsSearch {
  return { view: search.view, expiringWithinDays: search.expiringWithinDays };
}
